import React from 'react'
// libs
import styled from 'styled-components'
// components
import MaterialPopover, { PopoverProps } from '@mui/material/Popover'
// types
import { PaperProps } from '@mui/material/Paper'

const anchorOrigin = {
  vertical: 'bottom' as const,
  horizontal: 'left' as const
}

const paperProps = {
  onClick: (event: React.MouseEvent) => event.stopPropagation(),
  style: {
    borderRadius: 3,
    minHeight: 0,
    minWidth: 0
  }
}

const Popover = styled((props: PopoverProps & { width?: string }) => (
  <MaterialPopover
    {...props}
    slotProps={{
      paper: {
        ...paperProps,
        style: {
          ...paperProps.style,
          width: props.width
        },
        ...(props.slotProps.paper || {})
      }
    }}
  />
))``

const ItemsWrapper = styled.div``

interface Props extends Omit<PopoverProps, 'onClose' | 'open' | 'PaperProps' | 'children'> {
  width?: string
  onClose?: () => void
  trigger: React.ReactElement
  children: (props: { open: boolean; closePopup: (event?: React.MouseEvent) => void }) => React.ReactNode
  PaperProps?: Partial<PaperProps<React.ElementType>>
  onBackdropClick?: () => void
}

const PopoverModal: React.FC<Props> = ({
  trigger,
  onClose,
  children,
  className,
  PaperProps,
  onBackdropClick,
  ...props
}) => {
  const [anchor, setAnchor] = React.useState(null)

  const emptyAnchor = () => setAnchor(null)

  const isOpen = !!anchor

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    trigger.props.onClick?.(event)
    setAnchor(event.currentTarget)
  }

  const handleClose = (event: React.MouseEvent) => {
    event?.stopPropagation()
    onClose?.()
    emptyAnchor()
  }

  const handlePopoverClose = (_event: React.SyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      onBackdropClick?.()
    }

    onClose?.()
    emptyAnchor()
  }

  const onWrapperClick = (event: React.MouseEvent) => event.stopPropagation()

  if (!trigger) {
    return null
  }

  return (
    <>
      {React.cloneElement(trigger, {
        onClick: handleTriggerClick,
        'aria-haspopup': true,
        'aria-owns': isOpen ? 'PopoverModal' : undefined
      })}
      <Popover
        open={isOpen}
        anchorEl={anchor}
        id={'PopoverModal'}
        anchorOrigin={anchorOrigin}
        onClose={handlePopoverClose}
        slotProps={{ paper: PaperProps }}
        {...props}>
        <ItemsWrapper className={className} onClick={onWrapperClick}>
          {children({ open: !!anchor, closePopup: handleClose })}
        </ItemsWrapper>
      </Popover>
    </>
  )
}

export default PopoverModal
